/**
 * DOS518 - Digital Operations System for Wildfire Management
 * Copyright (c) 2024-2025 Angelo Irtilaç — All rights reserved.
 *
 * MicInput.tsx
 * Input e textarea con pulsante microfono integrato.
 * Riutilizzabili in tutto il form COAU.
 */

import React from 'react';
import { Capacitor } from '@capacitor/core';
import { useSpeechInput } from '../../hooks/useSpeechInput';

// ─── Stili inline condivisi ───────────────────────────────────

const micBtnStyle = (isListening: boolean): React.CSSProperties => ({
  position: 'absolute',
  right: 6,
  top: '50%',
  transform: 'translateY(-50%)',
  background: isListening ? '#dc2626' : 'rgba(249,115,22,0.15)',
  border: `1px solid ${isListening ? '#dc2626' : 'rgba(249,115,22,0.4)'}`,
  borderRadius: 4,
  padding: '2px 6px',
  cursor: 'pointer',
  fontSize: 13,
  lineHeight: 1,
  color: isListening ? '#fff' : '#f97316',
  boxShadow: isListening ? '0 0 8px #dc2626' : 'none',
  transition: 'all 0.2s',
  zIndex: 1,
});

const micBtnTextareaStyle = (isListening: boolean): React.CSSProperties => ({
  position: 'absolute',
  right: 6,
  top: 6,
  background: isListening ? '#dc2626' : 'rgba(249,115,22,0.15)',
  border: `1px solid ${isListening ? '#dc2626' : 'rgba(249,115,22,0.4)'}`,
  borderRadius: 4,
  padding: '2px 6px',
  cursor: 'pointer',
  fontSize: 13,
  lineHeight: 1,
  color: isListening ? '#fff' : '#f97316',
  boxShadow: isListening ? '0 0 8px #dc2626' : 'none',
  transition: 'all 0.2s',
  zIndex: 1,
});

const listeningBadge: React.CSSProperties = {
  fontSize: 10,
  color: '#f87171',
  marginTop: 2,
  display: 'block',
  animation: 'mic-pulse 1s ease-in-out infinite',
};

// ─── MicInput ────────────────────────────────────────────────

interface MicInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  onVoiceResult?: (text: string) => void;
  appendMode?: boolean; // se true, appende al valore esistente invece di sostituire
}

export const MicInput: React.FC<MicInputProps> = ({ onVoiceResult, onChange, style, appendMode = true, ...props }) => {
  const currentValueRef = React.useRef<string>('');

  // Aggiorna il ref ogni volta che il valore cambia
  React.useEffect(() => {
    currentValueRef.current = String(props.value ?? '');
  }, [props.value]);

  const { isListening, isSupported, isModelReady, error, clearError, startListening, stopListening } = useSpeechInput({
    onResult: (text) => {
      onVoiceResult?.(text);
      if (onChange) {
        const existing = currentValueRef.current;
        const newValue = appendMode && existing && existing !== 'Direzione'
          ? existing.trimEnd() + ' ' + text
          : text;
        const syntheticEvent = {
          target: { value: newValue },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
    },
    onError: (_msg) => { /* gestito tramite error state */ },
  });

  if (!isSupported) {
    return <input onChange={onChange} style={style} {...props} />;
  }

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <input
        onChange={onChange}
        style={{
          ...style,
          paddingRight: 36,
          outline: isListening ? '1px solid #dc2626' : undefined,
        }}
        {...props}
      />
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={isListening ? stopListening : startListening}
        style={micBtnStyle(isListening)}
        title={isListening ? 'Ferma dettatura' : isModelReady ? 'Ditta vocalmente' : 'Modello vocale in caricamento…'}
      >
        {isListening ? '⏹' : isModelReady ? '🎙' : '⏳'}
      </button>
      {isListening && (
        <span style={listeningBadge}>🔴 in ascolto…</span>
      )}
      {!isModelReady && !isListening && (
        <span style={{ ...listeningBadge, color: '#fbbf24', animation: 'none' }}>
          ⏳ Modello vocale in caricamento… [native:{String(Capacitor.isNativePlatform())}]
        </span>
      )}
      {error && !isListening && (
        <span
          style={{ ...listeningBadge, color: '#f87171', animation: 'none', cursor: 'pointer' }}
          onClick={clearError}
        >
          ⚠ {error}
        </span>
      )}
      <style>{`
        @keyframes mic-pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
};

// ─── MicTextarea ─────────────────────────────────────────────

interface MicTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  onVoiceResult?: (text: string) => void;
  appendMode?: boolean;
}

export const MicTextarea: React.FC<MicTextareaProps> = ({ onVoiceResult, onChange, style, appendMode = true, ...props }) => {
  const currentValueRef = React.useRef<string>('');

  React.useEffect(() => {
    currentValueRef.current = String(props.value ?? '');
  }, [props.value]);

  const { isListening, isSupported, isModelReady, startListening, stopListening } = useSpeechInput({
    onResult: (text) => {
      onVoiceResult?.(text);
      if (onChange) {
        const existing = currentValueRef.current;
        const newValue = appendMode && existing
          ? existing.trimEnd() + ' ' + text
          : text;
        const syntheticEvent = {
          target: { value: newValue },
        } as React.ChangeEvent<HTMLTextAreaElement>;
        onChange(syntheticEvent);
      }
    },
  });

  if (!isSupported) {
    return <textarea onChange={onChange} style={style} {...props} />;
  }

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <textarea
        onChange={onChange}
        style={{
          ...style,
          paddingRight: 36,
          outline: isListening ? '1px solid #dc2626' : undefined,
        }}
        {...props}
      />
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={isListening ? stopListening : startListening}
        style={micBtnTextareaStyle(isListening)}
        title={isListening ? 'Ferma dettatura' : 'Ditta vocalmente'}
      >
        {isListening ? '⏹' : '🎙'}
      </button>
      {isListening && (
        <span style={{ ...listeningBadge, position: 'absolute', bottom: 4, right: 6 }}>
          🔴 in ascolto…
        </span>
      )}
      {!isModelReady && !isListening && (
        <span style={{ ...listeningBadge, position: 'absolute', bottom: 4, right: 6, color: '#fbbf24', animation: 'none' }}>
          ⏳ modello in caricamento… [native:{String(Capacitor.isNativePlatform())}]
        </span>
      )}
    </div>
  );
};

// ─── parseVoiceTime ──────────────────────────────────────────
// Converte testo vocale italiano in formato "HH:MM"
// Es: "cinque e zero sei" → "05:06"  |  "17 30" → "17:30"

const ITALIAN_NUMS: Record<string, number> = {
  zero: 0, uno: 1, due: 2, tre: 3, quattro: 4,
  cinque: 5, sei: 6, sette: 7, otto: 8, nove: 9,
  dieci: 10, undici: 11, dodici: 12, tredici: 13,
  quattordici: 14, quindici: 15, sedici: 16, diciassette: 17,
  diciotto: 18, diciannove: 19,
  venti: 20, ventuno: 21, ventidue: 22, 'ventitré': 23, ventitre: 23,
  ventiquattro: 24, venticinque: 25, ventisei: 26, ventisette: 27,
  ventotto: 28, ventinove: 29,
  trenta: 30, trentuno: 31, trentadue: 32, 'trentatré': 33, trentatre: 33,
  trentaquattro: 34, trentacinque: 35, trentasei: 36, trentasette: 37,
  trentotto: 38, trentanove: 39,
  quaranta: 40, quarantuno: 41, quarantadue: 42, 'quarantatré': 43, quarantatre: 43,
  quarantaquattro: 44, quarantacinque: 45, quarantasei: 46, quarantasette: 47,
  quarantotto: 48, quarantanove: 49,
  cinquanta: 50, cinquantuno: 51, cinquantadue: 52, 'cinquantatré': 53, cinquantatre: 53,
  cinquantaquattro: 54, cinquantacinque: 55, cinquantasei: 56, cinquantasette: 57,
  cinquantotto: 58, cinquantanove: 59,
};

export function parseVoiceTime(text: string): string | null {
  const t = text.toLowerCase().trim();

  // Formato diretto: "5:06", "5.06", "05:06", "17h30"
  const direct = t.match(/^(\d{1,2})[:.h](\d{2})$/);
  if (direct) {
    const h = parseInt(direct[1]), m = parseInt(direct[2]);
    if (h <= 23 && m <= 59)
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  // Sostituisce parole italiane con numeri
  let s = t.replace(/\be\b/g, ' ');
  for (const [word, num] of Object.entries(ITALIAN_NUMS))
    s = s.replace(new RegExp(`\\b${word}\\b`, 'g'), String(num));

  // Tenta di estrarre due numeri (ore minuti)
  const parts = s.trim().split(/[\s:.,]+/).filter(Boolean);
  if (parts.length === 2) {
    const h = parseInt(parts[0]), m = parseInt(parts[1]);
    if (!isNaN(h) && !isNaN(m) && h <= 23 && m <= 59)
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  // Caso pronuncia cifra per cifra dei minuti: "cinque zero sei" → [5,0,6] → 05:06
  if (parts.length === 3 && parts[1].length === 1 && parts[2].length === 1) {
    const h = parseInt(parts[0]);
    const m = parseInt(parts[1] + parts[2]);
    if (!isNaN(h) && !isNaN(m) && h <= 23 && m <= 59)
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  return null;
}

// ─── MicTimeInput ─────────────────────────────────────────────

interface MicTimeInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  onVoiceResult?: (text: string) => void;
  onParseError?: (raw: string) => void;
}

export const MicTimeInput: React.FC<MicTimeInputProps> = ({
  onVoiceResult, onChange, onParseError, style, ...props
}) => {
  const [parseErr, setParseErr] = React.useState<string | null>(null);

  const { isListening, isSupported, startListening, stopListening } = useSpeechInput({
    onResult: (text) => {
      onVoiceResult?.(text);
      const time = parseVoiceTime(text);
      if (time) {
        setParseErr(null);
        if (onChange) {
          const ev = { target: { value: time } } as React.ChangeEvent<HTMLInputElement>;
          onChange(ev);
        }
      } else {
        const msg = `Non riconosciuto: "${text}"`;
        setParseErr(msg);
        onParseError?.(text);
      }
    },
  });

  if (!isSupported) {
    return <input type="time" onChange={onChange} style={style} {...props} />;
  }

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <input
        type="time"
        onChange={onChange}
        style={{
          ...style,
          paddingRight: 36,
          outline: isListening ? '1px solid #dc2626' : undefined,
        }}
        {...props}
      />
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={isListening ? stopListening : startListening}
        style={micBtnStyle(isListening)}
        title={isListening ? 'Ferma dettatura' : 'Ditta orario (es. "cinque e zero sei")'}
      >
        {isListening ? '⏹' : '🎙'}
      </button>
      {isListening && <span style={listeningBadge}>🔴 in ascolto…</span>}
      {parseErr && !isListening && (
        <span style={{ ...listeningBadge, color: '#f87171', animation: 'none' }}>
          ⚠ {parseErr}
        </span>
      )}
    </div>
  );
};

export default MicInput;
